
/* ====================================================
   THE ROOST — PAIR IT WITH CAROUSEL
   Navigation and scroll behaviour
   ==================================================== */

(() => {
  "use strict";

  const CAROUSEL_SELECTOR = "[data-pair-it-with]";

  function initialiseCarousel(carousel) {
    // Prevent duplicate initialisation.
    if (carousel.dataset.piwInitialised === "true") {
      return;
    }

    const viewport = carousel.querySelector(
      "[data-piw-viewport]"
    );

    const previousButton = carousel.querySelector(
      "[data-piw-prev]"
    );

    const nextButton = carousel.querySelector(
      "[data-piw-next]"
    );

    const track = carousel.querySelector(
      ".pair-it-with__track"
    );

    if (!viewport || !track) {
      return;
    }

    carousel.dataset.piwInitialised = "true";

    const cards = Array.from(
      track.querySelectorAll(".pair-it-with__card")
    );

    // Determine whether reduced motion is preferred.
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    function getScrollBehaviour() {
      return reducedMotion.matches ? "auto" : "smooth";
    }

    // Calculate the maximum horizontal scroll position.
    function getMaximumScroll() {
      return Math.max(
        0,
        viewport.scrollWidth - viewport.clientWidth
      );
    }

    // Calculate movement based on actual card width.
    function getScrollStep() {
      if (cards.length === 0) {
        return viewport.clientWidth;
      }

      const firstCard = cards[0];

      const styles = window.getComputedStyle(track);

      const gap = parseFloat(styles.columnGap) || 0;

      return firstCard.getBoundingClientRect().width + gap;
    }

    // Update navigation arrow availability.
    function updateNavigation() {
      const currentPosition = viewport.scrollLeft;
      const maximumPosition = getMaximumScroll();

      const tolerance = 2;

      const atStart = currentPosition <= tolerance;

      const atEnd =
        currentPosition >= maximumPosition - tolerance;

      if (previousButton) {
        previousButton.disabled =
          atStart || maximumPosition <= tolerance;

        previousButton.setAttribute(
          "aria-disabled",
          String(previousButton.disabled)
        );
      }

      if (nextButton) {
        nextButton.disabled =
          atEnd || maximumPosition <= tolerance;

        nextButton.setAttribute(
          "aria-disabled",
          String(nextButton.disabled)
        );
      }
    }

    // Move the carousel by approximately one product card.
    function moveCarousel(direction) {
      const step = getScrollStep();

      const targetPosition = Math.max(
        0,
        Math.min(
          getMaximumScroll(),
          viewport.scrollLeft + direction * step
        )
      );

      viewport.scrollTo({
        left: targetPosition,
        behavior: getScrollBehaviour()
      });
    }

    // Previous arrow.
    if (previousButton) {
      previousButton.addEventListener("click", () => {
        moveCarousel(-1);
      });
    }

    // Next arrow.
    if (nextButton) {
      nextButton.addEventListener("click", () => {
        moveCarousel(1);
      });
    }

    // Update arrow states during manual scrolling.
    let scrollUpdateRequested = false;

    viewport.addEventListener(
      "scroll",
      () => {
        if (scrollUpdateRequested) {
          return;
        }

        scrollUpdateRequested = true;

        window.requestAnimationFrame(() => {
          updateNavigation();
          scrollUpdateRequested = false;
        });
      },
      { passive: true }
    );

    // Update when the carousel width changes.
    if ("ResizeObserver" in window) {
      const resizeObserver = new ResizeObserver(() => {
        updateNavigation();
      });

      resizeObserver.observe(viewport);
      resizeObserver.observe(track);
    } else {
      window.addEventListener("resize", updateNavigation);
    }

    // Set initial navigation states.
    updateNavigation();

    // Recheck after the page has finished laying out.
    window.requestAnimationFrame(updateNavigation);

    // Allow product images and fonts to finish loading.
    window.addEventListener(
      "load",
      updateNavigation,
      { once: true }
    );
  }

  // Initialise every Pair It With carousel on the page.
  function initialiseAllCarousels(root = document) {
    const carousels = root.querySelectorAll(
      CAROUSEL_SELECTOR
    );

    carousels.forEach(initialiseCarousel);
  }

  // Initialise on normal page load.
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initialiseAllCarousels()
    );
  } else {
    initialiseAllCarousels();
  }

  // Support Shopify Theme Editor section reloads.
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initialiseAllCarousels(event.target);
    }
  );

})();
